import React, { useEffect, useState } from 'react';
import { X, RotateCcw, Loader } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { formatDateTime } from '../../utils/formatDate';

const STATUS_BADGE = {
  draft: 'bg-[#F3F3F3] text-[#706E6B]',
  published: 'bg-[rgba(4,132,75,0.1)] text-[#04844B]',
  archived: 'bg-[#F3F3F3] text-[#706E6B]',
};

/**
 * Phase 19 — version history/rollback UI. FormVersion immutability (every
 * schema edit forks a new draft rather than mutating a published version)
 * has existed since the builder's early phases; this is the first UI to
 * actually browse and restore from that history, previously only reachable
 * via the API.
 */
export default function VersionHistoryPanel({ formId, currentVersion, onClose, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringVersion, setRestoringVersion] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/forms/${formId}/versions`);
        setVersions(data.data || []);
      } catch (err) {
        alertService.toast('error', err.response?.data?.message || 'Failed to load version history');
      } finally {
        setLoading(false);
      }
    })();
  }, [formId]);

  const restore = async (version) => {
    if (!window.confirm(`Restore version ${version.version}? This copies that version's schema into your current draft — it won't be published until you publish again.`)) return;
    setRestoringVersion(version.version);
    try {
      await api.post(`/forms/${formId}/versions/${version.version}/restore`);
      alertService.toast('success', `Restored version ${version.version} into the current draft`);
      onRestored();
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to restore version');
    } finally {
      setRestoringVersion(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[480px] bg-white rounded-t-lg sm:rounded-lg shadow-xl p-4 sm:p-6 max-h-[100dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5] shrink-0">
          <p className="text-[16px] font-bold text-[#2E2E2E]">Version History</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader size={20} className="animate-spin text-[#706E6B]" /></div>
        ) : !versions.length ? (
          <p className="text-[13px] text-[#706E6B]">No versions yet.</p>
        ) : (
          <div className="space-y-1.5 overflow-y-auto">
            {versions.map((v) => (
              <div key={v._id} className="flex items-center justify-between px-3 py-2.5 border border-[#D8DDE6] rounded-[4px]">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-bold text-[#181818]">Version {v.version}</span>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${STATUS_BADGE[v.status] || STATUS_BADGE.draft}`}>{v.status}</span>
                    {v.version === currentVersion && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[rgba(1,118,211,0.1)] text-[#0176D3]">Editing</span>
                    )}
                  </div>
                  <p className="text-[11px] text-[#706E6B] mt-0.5">
                    {v.publishedAt ? `Published ${formatDateTime(v.publishedAt)}` : `Created ${formatDateTime(v.createdAt)}`}
                  </p>
                </div>
                {v.version !== currentVersion && (
                  <button
                    onClick={() => restore(v)}
                    disabled={restoringVersion !== null}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold text-[#0176D3] hover:bg-[rgba(1,118,211,0.06)] rounded-[4px] disabled:opacity-50 transition-colors"
                  >
                    {restoringVersion === v.version ? <Loader size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                    Restore
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
